import React from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import useExtension from 'flow/hooks/useExtension';
import useAppContext from 'flow/hooks/useAppContext';
import Button from 'flow/components/ui-components/Button';
import { sendPickupQueue } from 'flow/actions/top-actions';
import {
    addOmniChannelLogEntry,
    addTicketLogEntry,
} from 'flow/utils/browser-utils';
import { NewCallBlockReason } from 'flow/utils/call-utils';
import {
    hasActiveCallsBlockingNewCall,
    isRegistered,
} from 'flow/selectors/call-selectors';
import { routeUserToOmnichannelErrand } from 'flow/actions/omnichannel-actions';
import { loggedInUserHasOmnichannelFeature } from 'flow/selectors/extension-selectors';
import { selectOwnUserHasCapabilityTickets } from 'flow/selectors/TicketsSelectors';
import { isTeamsTypeIntegration } from 'flow/utils/app-utils';
import { useHistory } from 'react-router-dom';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import { QueueCallWithExtraData } from 'flow/types/Pbx';
import { TypedMap } from 'flow/types/TypedMap';

interface Props {
    queueItem: TypedMap<QueueCallWithExtraData>;
}

const PbxIncomingCallPickupButton = ({ queueItem }: Props) => {
    const { i18next, embedded } = useAppContext();
    const { ownExtension } = useExtension();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const userHasCapabilityTickets = useAppSelector(
        selectOwnUserHasCapabilityTickets,
    );

    const hasActiveCallBlockingNewCall = useAppSelector(
        (state) =>
            hasActiveCallsBlockingNewCall(state, 'incoming', 2) !==
            NewCallBlockReason.NONE,
    );
    const userHasOmnichannelFeature = useAppSelector(
        loggedInUserHasOmnichannelFeature,
    );
    const hasRegisteredSIP = useAppSelector((state) =>
        isRegistered(state, ownExtension),
    );

    const isTelavoxTicketNonCall = queueItem?.get('isTelavoxTicketNonCall');
    const isCentionOmniErrand = queueItem?.get('isOmnichannelErrand');

    const pickOmnichannelItem = () => {
        if (queueItem.get('telavoxOmniTicketId')) {
            //Omni ticket
            const ticketId = queueItem.get('telavoxOmniTicketId');
            addTicketLogEntry(
                `PBX View -> User pressed pick ticket ${ticketId}`,
            );
            history.push(
                generatePath(routes['tickets.ticketKey'], {
                    ticketKey: `ticket-${ticketId}`,
                }),
            );
        } else {
            //Cention errand
            dispatch(sendPickupQueue(queueItem.get('key'), 'main view'));
            addOmniChannelLogEntry('PBX View -> User pressed pick errand');
            dispatch(
                routeUserToOmnichannelErrand(
                    queueItem.get('omnichannelErrandDeepLinkId'),
                    'PBX main',
                ),
            );
        }
    };

    const shouldRenderPickPbxQueueItemButton = () => {
        if (hasActiveCallBlockingNewCall) {
            return false;
        }
        if (isTelavoxTicketNonCall) {
            return userHasCapabilityTickets;
        }
        if (isCentionOmniErrand) {
            return userHasOmnichannelFeature;
        }

        return hasRegisteredSIP;
    };

    const pickupQueueCall = () => {
        isCentionOmniErrand || isTelavoxTicketNonCall
            ? pickOmnichannelItem()
            : dispatch(sendPickupQueue(queueItem.get('key'), 'main view'));
    };

    const ButtonTitle = () => {
        if (isTelavoxTicketNonCall) return i18next.t('tickets.go_to');
        if (isCentionOmniErrand)
            return i18next.t('Queue.PbxQueueItem.Pick.Email');

        return i18next.t('Queue.CallsInQueue.PickCall.Take');
    };

    if (
        isTeamsTypeIntegration(embedded) ||
        !shouldRenderPickPbxQueueItemButton()
    )
        return null;

    return (
        <div className="flex h-6 gap-x-2">
            <Button
                color="ghost"
                className="!min-w-max text-xs"
                onClick={pickupQueueCall}
                data-test-id={
                    isCentionOmniErrand || isTelavoxTicketNonCall
                        ? 'pbx-waiting-omnichannel-errand-item-pickup-button'
                        : 'pbx-waiting-call-item-pickup-button'
                }
            >
                <ButtonTitle />
            </Button>
        </div>
    );
};

export default PbxIncomingCallPickupButton;
